import React, { useState, useEffect, Suspense, lazy, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WorldwideProvider, useWorldwide, WORLDWIDE_HIDDEN_TABS, FIRA_WW_LOGO } from './context/WorldwideContext';
import { BACKEND_URL } from './services/config';
import { checkAndRefreshToken, isTokenExpired } from './services/auth';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import AuthPage from './components/AuthPage';
import { ToastProvider } from './components/Toast';
import { ListSkeleton, StatsSkeleton } from './components/Skeleton';
import { LoadingWithTimeout } from './components/LoadingStates';
import ErrorBoundary from './components/ErrorBoundary';
import { getUserStatus, activateUser, getExchangeRate, getTradingAccounts, trackPageVisit, getGuideProgress, saveGuideProgress, getUserPreferences, updateUserPreferences } from './services/api';
import { LayoutDashboard, Calculator, BookOpen, TrendingUp, Brain, Settings, HelpCircle, GraduationCap, MessageCircle, Menu, X, CheckSquare, Trophy, BarChart3, Flame, FlaskConical, Workflow, Globe, Sun, Moon, Target, Crown, LogOut, Wallet, RefreshCw, Shield, User, Eye } from 'lucide-react';

// v7.6: Lazy load ALL tab components for faster initial load
// Only loads when user switches to that tab
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const FreeTrialCountdown = lazy(() => import('./components/worldwide/FreeTrialCountdown'));
const PremiumFeatures = lazy(() => import('./components/PremiumFeatures'));
const ProLeaguePage = lazy(() => import('./components/ProLeaguePage'));
const RiskCalculator = lazy(() => import('./components/RiskCalculator'));
const MarketSnapshot = lazy(() => import('./components/MarketSnapshot'));
const TradingJournal = lazy(() => import('./components/TradingJournal'));
const DrawdownAlert = lazy(() => import('./components/DrawdownAlert'));
const CompoundingCalculator = lazy(() => import('./components/CompoundingCalculator'));
const BreakEvenCalculator = lazy(() => import('./components/BreakEvenCalculator'));
const EconomicCalendar = lazy(() => import('./components/EconomicCalendar'));
const WeeklyReport = lazy(() => import('./components/WeeklyReport'));
const AIChat = lazy(() => import('./components/AIChat'));
const DashboardSummary = lazy(() => import('./components/DashboardSummary'));
const TargetTracker = lazy(() => import('./components/TargetTracker'));
const AccountManager = lazy(() => import('./components/AccountManager'));
const ExportData = lazy(() => import('./components/ExportData'));
const MT5Integration = lazy(() => import('./components/MT5Integration'));
const FeatureGuide = lazy(() => import('./components/FeatureGuide'));
const TradingEducation = lazy(() => import('./components/TradingEducation'));
const PipValueCalculator = lazy(() => import('./components/PipValueCalculator'));
const DailyChecklist = lazy(() => import('./components/DailyChecklist'));
const SessionAnalysis = lazy(() => import('./components/SessionAnalysis'));
const EmotionAnalysis = lazy(() => import('./components/EmotionAnalysis'));
const Achievements = lazy(() => import('./components/Achievements'));
const RiskHeatMap = lazy(() => import('./components/RiskHeatMap'));
const StrategyBuilder = lazy(() => import('./components/StrategyBuilder'));
const WalletDashboard = lazy(() => import('./components/WalletDashboard'));
const EquityCurve = lazy(() => import('./components/EquityCurve'));
const PolymarketPrediction = lazy(() => import('./components/PolymarketPrediction'));
const AIVisionAnalysis = lazy(() => import('./components/AIVisionAnalysis'));
const PreActivation = lazy(() => import('./components/PreActivation'));
const WorldwidePricingPage = lazy(() => import('./components/worldwide/PricingPage'));
const WorldwideLoginPopup = lazy(() => import('./components/worldwide/LoginEarlyBirdPopup'));
const WorldwideTelegramPopup = lazy(() => import('./components/worldwide/TelegramSubscribePopup'));
const WhatsAppFloat = lazy(() => import('./components/WhatsAppFloat'));
const MarketingAngle = lazy(() => import('./components/MarketingAngle'));
const LiveSupport = lazy(() => import('./components/LiveSupport'));
const SystemGuideModal = lazy(() => import('./components/SystemGuideModal'));
const LivePriceTicker = lazy(() => import('./components/LivePriceTicker'));
const MarketSessionBanner = lazy(() => import('./components/MarketSessionBanner'));
const SignalPerformance = lazy(() => import('./components/SignalPerformance'));
const Feedback = lazy(() => import('./components/Feedback'));
const TradeGuardian = lazy(() => import('./components/TradeGuardian'));
const SmartCapital = lazy(() => import('./components/SmartCapital'));
const ProfileSettings = lazy(() => import('./components/ProfileSettings'));
const LandingPage = lazy(() => import('./components/landing/LandingPage'));
const HomePage = lazy(() => import('./components/homepage/HomePage'));
const AIChartVisionPage = lazy(() => import('./components/features/AIChartVisionPage'));
const CurrencyStrengthPage = lazy(() => import('./components/features/CurrencyStrengthPage'));
const PremiumSignalsPage = lazy(() => import('./components/features/PremiumSignalsPage'));
const SignalPerformancePage = lazy(() => import('./components/features/SignalPerformancePage'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const AboutUs = lazy(() => import('./components/AboutUs'));

// Loading fallback component for lazy loaded components
const LazyLoadFallback = () => (
  <LoadingWithTimeout 
    isLoading={true} 
    loadingText="Memuatkan modul..."
    slowText="Modul agak besar, sila tunggu..."
  >
    <div />
  </LoadingWithTimeout>
);

// Legacy invite links point to /?invite=CODE (root was the auth page before the
// marketing homepage). Redirect those to /login so registration still works.
const HomeOrInviteRedirect = () => {
  const search = window.location.search;
  if (new URLSearchParams(search).get('invite')) {
    return <Navigate to={`/login${search}`} replace />;
  }
  return <Suspense fallback={<LazyLoadFallback />}><HomePage /></Suspense>;
};

// v7.6: Lightweight tab loading indicator
const TabFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px', opacity: 0.6 }}>
    <div style={{ width: '24px', height: '24px', border: '3px solid var(--border-subtle)', borderTopColor: 'var(--accent-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>
);

function AppContent() {
  const { isAuthenticated, loading: authLoading, user, logout } = useAuth();
  const { isWorldwide } = useWorldwide();
  const [isActivated, setIsActivated] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(4.47);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    // Valid tabs that have rendering in App.js
    const validTabs = ['guide', 'checklist', 'journal', 'ai', 'tools', 'premium', 'proleague', 'aivision', 'smartcapital', 'performance', 'polymarket', 'education', 'achievements', 'feedback', 'guardian', 'profile', 'pricing'];
    
    // Check URL ?tab= param first (from email CTA links)
    const urlTab = new URLSearchParams(window.location.search).get('tab');
    if (urlTab && validTabs.includes(urlTab)) {
      return urlTab;
    }

    // Restore activeTab from localStorage if available (for language switch)
    const savedTab = localStorage.getItem('fira-active-tab');
    if (savedTab) {
      localStorage.removeItem('fira-active-tab'); // Clear after use
      // Validate saved tab - fallback to journal if invalid
      if (validTabs.includes(savedTab)) {
        return savedTab;
      }
      console.warn(`[App] Invalid saved tab "${savedTab}", falling back to journal`);
    }
    // Check if guide is already complete - go to journal (main dashboard) instead
    const guideAlreadyComplete = localStorage.getItem('fira-quiz-complete') === 'true';
    return guideAlreadyComplete ? 'journal' : 'guide';
  });
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [tradingAccounts, setTradingAccounts] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLightTheme, setIsLightTheme] = useState(false);
  const { t, language } = useLanguage();
  
  // Admin panel state - 7 clicks on logo to access (whitelist protected)
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [adminWhitelistChecked, setAdminWhitelistChecked] = useState(false);
  
  // Page tracking for BI
  const [pageStartTime, setPageStartTime] = useState(Date.now());

  // Guide completion state - check localStorage first, then sync with backend
  const [guideComplete, setGuideComplete] = useState(() => {
    return localStorage.getItem('fira-quiz-complete') === 'true';
  });
  const [showGuideReminder, setShowGuideReminder] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  
  // Refresh key - increments when tab changes to force data refresh
  const [refreshKey, setRefreshKey] = useState(0);

  // Worldwide conversion popups (Batch C)
  const [showWwLoginPopup, setShowWwLoginPopup] = useState(false);
  const [showWwTelegramPopup, setShowWwTelegramPopup] = useState(false);

  // Worldwide mode: redirect to 'premium' if current tab is hidden
  useEffect(() => {
    if (isWorldwide && WORLDWIDE_HIDDEN_TABS.includes(activeTab)) {
      setActiveTab('premium');
    }
  }, [isWorldwide, activeTab]);

  // Listen for programmatic tab-switch events (e.g., from FreeTrialCountdown CTA)
  useEffect(() => {
    const handleNavigate = (e) => {
      const target = e.detail;
      if (typeof target === 'string') setActiveTab(target);
    };
    window.addEventListener('fira:navigate-tab', handleNavigate);
    return () => window.removeEventListener('fira:navigate-tab', handleNavigate);
  }, []);

  // Worldwide conversion popups (Batch C):
  // 1. Login popup — every login (session-scoped)
  // 2. Telegram popup — after login popup dismissed, until user confirms subscribed
  useEffect(() => {
    if (!isAuthenticated || !isWorldwide) return;

    // Login popup: show once per session (per browser tab)
    const sessionKey = 'ww_login_popup_session';
    const shown = sessionStorage.getItem(sessionKey);
    if (!shown) {
      // Small delay so the dashboard renders first
      const timer = setTimeout(() => {
        setShowWwLoginPopup(true);
        sessionStorage.setItem(sessionKey, '1');
      }, 1200);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isWorldwide]);

  // Force dark mode always
  useEffect(() => {
    document.documentElement.classList.add('dark-mode');
    localStorage.setItem('fira-theme', 'dark');
  }, []);

  // Fetch user preferences (theme, language) from backend on login
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchUserPreferences = async () => {
      try {
        const result = await getUserPreferences();
        if (result.success && result.preferences) {
          const prefs = result.preferences;
          // Dark mode only — ignore theme preference from backend
        }
      } catch (error) {
        console.debug('[PREFS] Failed to fetch user preferences:', error.message);
      }
    };
    
    fetchUserPreferences();
  }, [isAuthenticated]);

  // Fetch guide progress from backend when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchGuideProgress = async () => {
      try {
        const progress = await getGuideProgress();
        if (progress.quiz_complete) {
          setGuideComplete(true);
          localStorage.setItem('fira-quiz-complete', 'true');
        }
      } catch (error) {
        console.error('Error fetching guide progress:', error);
      }
    };
    
    fetchGuideProgress();
  }, [isAuthenticated]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Increment refreshKey when tab changes to force data refresh
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [activeTab]);

  // Listen for LiveSupport tab navigation events
  useEffect(() => {
    const handleNavigateToTab = (e) => {
      setActiveTab(e.detail);
      setSidebarOpen(false);
    };
    
    window.addEventListener('navigateToTab', handleNavigateToTab);
    
    return () => {
      window.removeEventListener('navigateToTab', handleNavigateToTab);
    };
  }, []);

  // Token refresh on app resume/visibility change
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.debug('[APP] App became visible, checking token...');
        
        // Check if token is expired
        if (isTokenExpired()) {
          console.debug('[APP] Token expired, logging out...');
          logout();
          return;
        }
        
        // Try to refresh token if expiring soon
        const tokenValid = await checkAndRefreshToken();
        if (!tokenValid) {
          console.debug('[APP] Token refresh failed, logging out...');
          logout();
        }
      }
    };
    
    // Check token on mount
    handleVisibilityChange();
    
    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also check periodically (every 5 minutes)
    const intervalId = setInterval(handleVisibilityChange, 5 * 60 * 1000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [isAuthenticated, logout]);

  useEffect(() => {
    // Only fetch data if authenticated
    if (!isAuthenticated) return;
    
    const fetchInitialData = async () => {
      // Quick start - show app immediately, then load data
      setLoading(false);
      setIsActivated(true);
      setExchangeRate(4.47);
      
      // Load ALL data in parallel for faster startup
      try {
        const [userStatus, rateData, accounts] = await Promise.all([
          getUserStatus().catch(() => ({ is_activated: true })),
          getExchangeRate().catch(() => ({ usd_to_myr: 4.47 })),
          getTradingAccounts().catch(() => [])
        ]);
        
        setIsActivated(userStatus.is_activated);
        setExchangeRate(rateData.usd_to_myr);
        setTradingAccounts(accounts);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    fetchInitialData();
  }, [isAuthenticated]);

  // Redirect to login if not authenticated (MUST be after all hooks)
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Show loading while checking auth
  if (authLoading) {
    return <LazyLoadFallback />;
  }

  const handleActivate = async () => {
    try {
      await activateUser('DEMO-123456');
      setIsActivated(true);
    } catch (error) {
      console.error('Error activating user:', error);
      setIsActivated(true);
    }
  };

  const handleGuideComplete = async () => {
    setGuideComplete(true);
    localStorage.setItem('fira-quiz-complete', 'true');
    
    // Save to backend so it persists across devices/sessions
    try {
      await saveGuideProgress({
        completed_sections: JSON.parse(localStorage.getItem('fira-guide-progress') || '[]'),
        quiz_complete: true
      });
    } catch (error) {
      console.error('Error saving guide progress:', error);
    }
  };

  const handleTabChange = (tabId) => {
    // Valid tabs that have rendering in App.js
    const validTabs = ['guide', 'checklist', 'journal', 'ai', 'tools', 'premium', 'proleague', 'aivision', 'smartcapital', 'performance', 'polymarket', 'education', 'achievements', 'feedback', 'guardian', 'profile', 'pricing'];
    
    // Validate tab before changing - fallback to journal/premium if invalid
    if (!validTabs.includes(tabId)) {
      console.warn(`[App] Invalid tab "${tabId}", falling back`);
      tabId = isWorldwide ? 'premium' : 'journal';
    }

    // Block hidden tabs in worldwide mode
    if (isWorldwide && WORLDWIDE_HIDDEN_TABS.includes(tabId)) {
      tabId = 'premium';
    }
    
    // Track page visit duration for BI
    const duration = Math.floor((Date.now() - pageStartTime) / 1000);
    if (activeTab && duration > 0) {
      trackPageVisit(activeTab, duration);
    }
    setPageStartTime(Date.now());
    
    // If guide not complete and trying to navigate away from guide, show reminder
    if (!guideComplete && tabId !== 'guide' && activeTab === 'guide') {
      setShowGuideReminder(true);
      setPendingTab(tabId);
      return;
    }
    setActiveTab(tabId);
    setSidebarOpen(false);
  };
  
  // Handle logo click for hidden admin panel (whitelist protected)
  const handleLogoClick = async () => {
    const now = Date.now();
    // Reset count if more than 2 seconds since last click
    if (now - lastClickTime > 2000) {
      setLogoClickCount(1);
    } else {
      setLogoClickCount(prev => prev + 1);
    }
    setLastClickTime(now);
    
    // Open admin panel after 7 clicks - but only if whitelisted
    if (logoClickCount >= 6) {
      setLogoClickCount(0);
      
      // Check whitelist silently - no error shown if not allowed
      try {
        const token = localStorage.getItem('fira_auth_token');
        if (!token) return; // Silent fail - not logged in
        
        const API_URL = BACKEND_URL;
        const response = await fetch(`${API_URL}/api/admin/whitelist/check`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        
        if (data.allowed) {
          setShowAdminPanel(true);
          setAdminWhitelistChecked(true);
        }
        // Silent fail if not allowed - nothing happens
      } catch (e) {
        // Silent fail - no error shown
        console.debug('[Admin] Access check failed');
      }
    }
  };

  const handleSkipGuide = () => {
    setShowGuideReminder(false);
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
    setSidebarOpen(false);
  };

  // Skip guide permanently - mark as complete and don't show reminder again
  const handleSkipGuidePermanently = async () => {
    setShowGuideReminder(false);
    setGuideComplete(true);
    localStorage.setItem('fira-quiz-complete', 'true');
    
    // Save to backend
    try {
      await saveGuideProgress({
        completed_sections: ['skipped'],
        skipped: true,
        skipped_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving guide skip:', error);
    }
    
    if (pendingTab) {
      setActiveTab(pendingTab);
      setPendingTab(null);
    }
    setSidebarOpen(false);
  };

  const handleBackToGuide = () => {
    setShowGuideReminder(false);
    setPendingTab(null);
  };

  // If auth is still loading, show loading spinner
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-300">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return <AuthPage />;
  }

  if (loading) {
    return (
      <div className="app-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', padding: '20px', width: '100%', maxWidth: '400px' }}>
          <div style={{ 
            width: '50px', 
            height: '50px', 
            border: '3px solid var(--border-subtle)', 
            borderTop: '3px solid #FF4081', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite', 
            margin: '0 auto 20px' 
          }} />
          <div style={{ color: '#FF4081', fontWeight: '600', marginBottom: '20px' }}>Loading Fira Smart Desk...</div>
          <StatsSkeleton />
        </div>
      </div>
    );
  }
  
  // Show Admin Panel if activated (with lazy loading)
  if (showAdminPanel) {
    return (
      <Suspense fallback={<LazyLoadFallback />}>
        <AdminPanel onBack={() => setShowAdminPanel(false)} />
      </Suspense>
    );
  }

  // Worldwide users skip the Malaysia-only broker activation gate
  // (they get Premium directly via invite link). Local users still go through it.
  if (!isActivated && !isWorldwide) {
    return (
      <div className="app-container">
        <PreActivation onActivate={handleActivate} />
        {/* WhatsApp Float removed - no function */}
      </div>
    );
  }

  const allTabs = [
    { id: 'guide', labelKey: 'menu.guide', icon: HelpCircle },
    // { id: 'checklist', labelKey: 'menu.checklist', icon: CheckSquare },  // Hidden from MY + WW sidebar (component preserved for legacy links)
    { id: 'journal', labelKey: 'menu.journal', icon: BookOpen },
    // { id: 'ai', label: 'AI Trading Assistant', icon: MessageCircle },  // Hidden from MY + WW sidebar (component preserved for legacy links)
    { id: 'tools', labelKey: 'menu.tools', icon: Calculator },
    { id: 'premium', label: '⭐ Premium (Signal)', icon: Crown, highlight: true },
    // Fira Pro League — public-facing trading contest leaderboard (Feb 2026).
    // Visible to BOTH MY and WW users right below Premium so it benefits
    // from the highest-attention area of the sidebar.
    { id: 'proleague', label: 'Pro League', icon: Trophy, badge: 'LIVE', highlight: true },
    { id: 'aivision', label: 'AI Vision', icon: Eye, badge: 'New', highlight: true },
    { id: 'smartcapital', label: 'Smart Capital CT', icon: TrendingUp, badge: 'Hot🔥' },
    // { id: 'guardian', label: 'Trade Guardian', icon: Shield, badge: 'Beta' },  // Disabled — cost optimization
    // { id: 'performance', label: 'Signal Performance', icon: Trophy },  // Hidden from MY + WW sidebar (component preserved for legacy links)
    { id: 'polymarket', labelKey: 'menu.polymarket', icon: BarChart3 },
    { id: 'education', labelKey: 'menu.education', icon: GraduationCap },
    // { id: 'achievements', labelKey: 'menu.achievements', icon: Trophy },  // Hidden from MY + WW sidebar (component preserved for legacy links)
    { id: 'feedback', label: 'Feedback', icon: MessageCircle },
    { id: 'profile', label: language === 'bm' || language === 'ms' ? 'Profil & Tetapan' : 'Profile & Settings', icon: User },
  ];

  // Filter out hidden tabs for worldwide users
  // Also inject the 'pricing' (Full Access offer) tab immediately after Guide
  // so it's the second item in the WW sidebar, and keep Profile as the last item.
  const tabs = isWorldwide
    ? (() => {
        const visible = allTabs.filter(tab => !WORLDWIDE_HIDDEN_TABS.includes(tab.id) && tab.id !== 'profile');
        const guideIdx = visible.findIndex(t => t.id === 'guide');
        const pricingTab = { id: 'pricing', label: '💎 Full Access', icon: Crown, badge: 'Offer', highlight: true };
        const beforeProfile = [
          ...visible.slice(0, guideIdx + 1),
          pricingTab,
          ...visible.slice(guideIdx + 1),
        ];
        return [...beforeProfile, ...allTabs.filter(tab => tab.id === 'profile')];
      })()
    : allTabs;

  return (
    <div className="app-container" style={{ display: 'flex' }}>
      {/* Worldwide Conversion Popups (Batch C) */}
      {showWwLoginPopup && isWorldwide && (
        <Suspense fallback={null}>
          <WorldwideLoginPopup
            onClose={() => {
              setShowWwLoginPopup(false);
              // After login popup, consider showing Telegram popup
              // for free-trial users who haven't yet subscribed
              const subscribed = localStorage.getItem('ww_telegram_subscribed') === '1';
              const dismissed = sessionStorage.getItem('ww_telegram_popup_dismissed') === '1';
              if (!subscribed && !dismissed) {
                setTimeout(() => setShowWwTelegramPopup(true), 400);
              }
            }}
            onNavigateToPricing={() => setActiveTab('pricing')}
          />
        </Suspense>
      )}

      {showWwTelegramPopup && isWorldwide && (
        <Suspense fallback={null}>
          <WorldwideTelegramPopup
            onMarkSubscribed={() => {
              localStorage.setItem('ww_telegram_subscribed', '1');
              setShowWwTelegramPopup(false);
            }}
            onDismiss={() => {
              // Remember dismissal for this session only — popup will
              // re-appear next login if still not subscribed.
              sessionStorage.setItem('ww_telegram_popup_dismissed', '1');
              setShowWwTelegramPopup(false);
            }}
          />
        </Suspense>
      )}

      {/* Desktop Sidebar - Always Visible */}
      {!isMobile && (
        <div 
          className="desktop-sidebar"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '260px',
            height: '100vh',
            background: 'var(--bg-card)',
            borderRight: '1px solid var(--border-subtle)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Sidebar Header */}
          <div style={{
            padding: '16px 18px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer'
          }}
          onClick={handleLogoClick}
          data-testid="logo-click-area"
          >
            <img
              src={isWorldwide ? FIRA_WW_LOGO : 'https://customer-assets.emergentagent.com/job_ringgit-trade-hub/artifacts/31oc9nj4_IMG_0100.jpeg'}
              alt={isWorldwide ? 'Fira Worldwide Logo' : 'Fira Logo'}
              style={{ width: '38px', height: '38px', borderRadius: '10px', border: '2px solid #FF4081' }}
            />
            <div>
              <div style={{ fontWeight: '700', fontSize: '15px', color: '#FF4081', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {isWorldwide ? 'Fira Worldwide' : 'Fira Smart Desk'}
                {isWorldwide && (
                  <span
                    data-testid="free-trial-badge-sidebar"
                    style={{
                      padding: '2px 6px',
                      fontSize: '8px',
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: '#FFFFFF',
                      background: 'linear-gradient(135deg, #FF4081 0%, #E91E63 100%)',
                      borderRadius: '4px',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Free Trial
                  </span>
                )}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{isWorldwide ? 'Global Edition' : 'Trading Assistant'}</div>
            </div>
          </div>

          {/* Menu Items */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
            {tabs.map((tab) => {
              const isPremiumItem = tab.id === 'premium';
              const isActive = activeTab === tab.id;
              const isLocked = tab.locked;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => !isLocked && handleTabChange(tab.id)}
                  data-testid={`menu-${tab.id}`}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: isPremiumItem ? '14px 14px' : '12px 14px',
                    marginBottom: isPremiumItem ? '6px' : '3px',
                    marginTop: isPremiumItem ? '6px' : '0',
                    background: isLocked 
                      ? 'rgba(100, 100, 100, 0.1)'
                      : isPremiumItem 
                        ? 'linear-gradient(135deg, #FF4081 0%, #E91E63 50%, #AD1457 100%)'
                        : isActive 
                          ? '#FF4081'
                          : 'transparent',
                    border: isLocked ? '1px dashed var(--border-subtle)' : 'none',
                    borderRadius: isPremiumItem ? '12px' : '10px',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    color: isLocked 
                      ? 'var(--text-muted)' 
                      : isPremiumItem ? '#FFFFFF' : isActive ? '#FFFFFF' : 'var(--text-primary)',
                    fontSize: '14px',
                    fontWeight: isPremiumItem ? '700' : isActive ? '600' : '500',
                    transition: 'all 0.2s ease',
                    textAlign: 'left',
                    boxShadow: isPremiumItem 
                      ? '0 4px 15px rgba(255, 64, 129, 0.4), 0 0 15px rgba(255, 64, 129, 0.2)' 
                      : 'none',
                    position: 'relative',
                    overflow: 'hidden',
                    opacity: isLocked ? 0.6 : 1
                  }}
                >
                  {/* Shimmer effect for Premium */}
                  {isPremiumItem && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      animation: 'premiumShimmer 2s infinite'
                    }} />
                  )}
                  <tab.icon size={18} style={{ 
                    position: 'relative', 
                    zIndex: 1,
                    filter: isPremiumItem ? 'drop-shadow(0 0 3px rgba(255,255,255,0.5))' : 'none',
                    opacity: isLocked ? 0.5 : 1
                  }} />
                  <span style={{ position: 'relative', zIndex: 1, flex: 1 }}>
                    {isPremiumItem ? '⭐ Premium (Signal)' : (tab.label || t(tab.labelKey))}
                  </span>
                  {/* Locked Badge */}
                  {tab.badge && (
                    <span style={{
                      background: tab.badge === 'New' ? 'linear-gradient(135deg, #FF4081 0%, #F50057 100%)' : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '9px',
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      position: 'relative',
                      zIndex: 1,
                      color: '#fff',
                      textTransform: 'uppercase'
                    }}>
                      {tab.badge}
                    </span>
                  )}
                  {isPremiumItem && (
                    <span style={{
                      background: 'rgba(255,255,255,0.25)',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      fontSize: '8px',
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      VIP
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Sidebar Footer */}
          <div style={{
            padding: '14px 16px',
            borderTop: '1px solid var(--border-subtle)',
          }}>
            {/* User Info */}
            {user && (
              <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                  {user.name}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  {user.email}
                </div>
              </div>
            )}
            {/* Logout Button */}
            <button
              onClick={logout}
              data-testid="logout-btn-desktop"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                padding: '10px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '8px'
              }}
            >
              <LogOut size={16} />
              {language === 'bm' || language === 'ms' ? 'Log Keluar' : 'Logout'}
            </button>
            <div style={{
              fontSize: '11px',
              color: 'var(--text-muted)',
              textAlign: 'center'
            }}>
              {isWorldwide ? 'AI-Powered Trading Signals' : t('footer.tagline')}
            </div>
            <div data-testid="powered-by-desktop" style={{
              fontSize: '10px',
              color: '#64748b',
              textAlign: 'center',
              marginTop: '6px',
              letterSpacing: '0.5px'
            }}>
              Powered by <span style={{ color: '#FF4081', fontWeight: '600' }}>{isWorldwide ? 'Fira Smart Desk' : 'Underads'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {isMobile && sidebarOpen && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Mobile Sidebar Menu */}
      {isMobile && (
        <div 
          className="sidebar-menu"
          style={{
            position: 'fixed',
            top: 0,
            left: sidebarOpen ? 0 : '-280px',
            width: '280px',
            height: '100vh',
            background: 'var(--bg-card)',
            borderRight: '1px solid var(--border-subtle)',
            zIndex: 999,
            transition: 'left 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: sidebarOpen ? '4px 0 20px rgba(0,0,0,0.15)' : 'none'
          }}
        >
          {/* Sidebar Header */}
          <div style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div 
              style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
              onClick={handleLogoClick}
              data-testid="logo-click-area-mobile"
            >
              <img
                src={isWorldwide ? FIRA_WW_LOGO : 'https://customer-assets.emergentagent.com/job_ringgit-trade-hub/artifacts/31oc9nj4_IMG_0100.jpeg'}
                alt={isWorldwide ? 'Fira Worldwide Logo' : 'Fira Logo'}
                style={{ width: '40px', height: '40px', borderRadius: '10px', border: '2px solid #FF4081' }}
              />
              <div>
                <div style={{ fontWeight: '700', fontSize: '16px', color: '#FF4081', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {isWorldwide ? 'Fira Worldwide' : 'Fira Smart Desk'}
                  {isWorldwide && (
                    <span
                      data-testid="free-trial-badge-mobile"
                      style={{
                        padding: '2px 6px',
                        fontSize: '8px',
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        color: '#FFFFFF',
                        background: 'linear-gradient(135deg, #FF4081 0%, #E91E63 100%)',
                        borderRadius: '4px',
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Free Trial
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{isWorldwide ? 'Global Edition' : 'Trading Assistant'}</div>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                padding: '8px'
              }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Menu Items */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
            {tabs.map((tab) => {
              const isPremiumItem = tab.id === 'premium';
              const isActive = activeTab === tab.id;
              const isLocked = tab.locked;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => !isLocked && handleTabChange(tab.id)}
                  disabled={isLocked}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    padding: isPremiumItem ? '14px 16px' : '14px 16px',
                    marginBottom: isPremiumItem ? '8px' : '4px',
                    marginTop: isPremiumItem ? '4px' : '0',
                    background: isLocked 
                      ? 'rgba(100, 100, 100, 0.1)'
                      : isPremiumItem 
                        ? isActive 
                          ? 'linear-gradient(135deg, #FF4081 0%, #F50057 100%)' 
                          : 'linear-gradient(135deg, #FF4081 0%, #E91E63 50%, #AD1457 100%)'
                        : isActive 
                          ? '#FF4081' 
                          : 'transparent',
                    border: isLocked ? '1px dashed var(--border-subtle)' : 'none',
                    borderRadius: isPremiumItem ? '14px' : '12px',
                    cursor: isLocked ? 'not-allowed' : 'pointer',
                    color: isLocked 
                      ? 'var(--text-muted)' 
                      : isPremiumItem ? '#FFFFFF' : isActive ? '#FFFFFF' : 'var(--text-primary)',
                    fontSize: isPremiumItem ? '15px' : '15px',
                    fontWeight: isPremiumItem ? '700' : isActive ? '600' : '500',
                    transition: 'all 0.3s ease',
                    textAlign: 'left',
                    boxShadow: isPremiumItem 
                      ? '0 4px 15px rgba(255, 64, 129, 0.4), 0 0 20px rgba(255, 64, 129, 0.2)' 
                      : 'none',
                    position: 'relative',
                    overflow: 'hidden',
                    opacity: isLocked ? 0.6 : 1
                  }}
                >
                  {/* Shimmer effect for Premium */}
                  {isPremiumItem && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      animation: 'premiumShimmer 2s infinite'
                    }} />
                  )}
                  <tab.icon size={20} style={{ 
                    position: 'relative', 
                    zIndex: 1,
                    filter: isPremiumItem ? 'drop-shadow(0 0 4px rgba(255,255,255,0.5))' : 'none',
                    opacity: isLocked ? 0.5 : 1
                  }} />
                  <span style={{ position: 'relative', zIndex: 1, flex: 1 }}>
                    {isPremiumItem ? '⭐ Premium (Signal)' : (tab.label || t(tab.labelKey))}
                  </span>
                  {/* Locked Badge */}
                  {tab.badge && (
                    <span style={{
                      background: tab.badge === 'New' ? 'linear-gradient(135deg, #FF4081 0%, #F50057 100%)' : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '9px',
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      position: 'relative',
                      zIndex: 1,
                      color: '#fff',
                      textTransform: 'uppercase'
                    }}>
                      {tab.badge}
                    </span>
                  )}
                  {isPremiumItem && (
                    <span style={{
                      marginLeft: 'auto',
                      background: 'rgba(255,255,255,0.25)',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '9px',
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      VIP
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Sidebar Footer */}
          <div style={{
            padding: '16px 20px',
            borderTop: '1px solid var(--border-subtle)',
          }}>
            {/* User Info */}
            {user && (
              <div style={{ marginBottom: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: '500' }}>
                  {user.name}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {user.email}
                </div>
              </div>
            )}
            {/* Logout Button */}
            <button
              onClick={logout}
              data-testid="logout-btn-mobile"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: '100%',
                padding: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '8px'
              }}
            >
              <LogOut size={18} />
              {language === 'bm' || language === 'ms' ? 'Log Keluar' : 'Logout'}
            </button>
            <div style={{
              fontSize: '12px',
              color: 'var(--text-muted)',
              textAlign: 'center'
            }}>
              {isWorldwide ? 'AI-Powered Trading Signals' : t('footer.tagline')}
            </div>
            <div data-testid="powered-by-mobile" style={{
              fontSize: '10px',
              color: '#64748b',
              textAlign: 'center',
              marginTop: '6px',
              letterSpacing: '0.5px'
            }}>
              Powered by <span style={{ color: '#FF4081', fontWeight: '600' }}>{isWorldwide ? 'Fira Smart Desk' : 'Underads'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div style={{ 
        marginLeft: isMobile ? 0 : '260px', 
        width: isMobile ? '100%' : 'calc(100% - 260px)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Live Price Ticker - DISABLED (saves server resources for 500 concurrent users) */}
        {/* <LivePriceTicker symbols={['XAU/USD', 'EUR/USD', 'GBP/USD', 'USD/JPY']} /> */}
        
        {/* Header Bar - ALWAYS VISIBLE */}
        <header 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            width: '100%',
            maxWidth: '100vw',
            height: '56px',
            padding: '8px 12px',
            background: 'linear-gradient(135deg, #FF4081 0%, #F50057 100%)',
            zIndex: 99,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            boxSizing: 'border-box'
          }}
        >
          {/* Hamburger Menu */}
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(true)}
              style={{
                background: 'rgba(255,255,255,0.25)',
                border: 'none',
                borderRadius: '20px',
                padding: '10px 14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
              }}
            >
              <Menu size={20} />
            </button>
          )}
          
          {/* Exchange Rate */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '6px 12px',
            background: 'rgba(255, 255, 255, 0.25)',
            borderRadius: '20px'
          }}>
            {!isWorldwide && (
            <>
            <span style={{ fontSize: '8px', color: 'rgba(255, 255, 255, 0.9)', fontWeight: '500' }}>USD / MYR</span>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#FFFFFF' }}>RM {exchangeRate.toFixed(2)}</span>
            </>
            )}
          </div>
          
          {/* Spacer */}
          <div style={{ flex: 1 }} />
          
          {/* Refresh Button */}
          <button
            onClick={() => {
              if ('caches' in window) caches.keys().then(n => n.forEach(name => caches.delete(name)));
              window.location.reload();
            }}
            data-testid="refresh-button"
            style={{
              background: 'rgba(255, 255, 255, 0.25)',
              border: 'none',
              color: '#FFFFFF',
              padding: '10px 14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '20px'
            }}
          >
            <RefreshCw size={18} />
          </button>
          
          {/* Language Toggle — hidden in worldwide mode */}
          {!isWorldwide && (
          <button
            onClick={() => {
              const current = localStorage.getItem('fira-language') || 'bm';
              localStorage.setItem('fira-language', current === 'bm' ? 'en' : 'bm');
              localStorage.setItem('fira-active-tab', activeTab);
              window.location.reload();
            }}
            data-testid="language-toggle"
            style={{
              background: 'rgba(255, 255, 255, 0.25)',
              border: 'none',
              color: '#FFFFFF',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: '600'
            }}
          >
            <Globe size={14} />
            {(localStorage.getItem('fira-language') || 'bm') === 'bm' ? '🇲🇾 BM' : '🇬🇧 EN'}
          </button>
          )}
        </header>
        
        <main className="main-content" style={{ paddingBottom: isMobile ? '80px' : '20px', flex: 1 }}>
          <div className="dashboard-container">

            {/* Global WW free-trial progress banner — visible across all tabs
                for active trial users. Renders "Day N of 7" + progress bar
                + countdown + "Unlock via Deposit" CTA. */}
            {isWorldwide && user?.is_free_trial && user?.premium_expiry && (
              <Suspense fallback={null}>
                <FreeTrialCountdown
                  user={user}
                  trialEndIso={user.premium_expiry}
                  onNavigateToPricing={() => setActiveTab('pricing')}
                  compact
                />
              </Suspense>
            )}

            {/* Daily Checklist Tab */}
            {activeTab === 'checklist' && (
              <Suspense fallback={<TabFallback />}>
              <div className="dashboard-grid">
                <div style={{ gridColumn: '1 / -1' }}>
                  <DailyChecklist key={refreshKey} onComplete={() => {}} />
                </div>
              </div>
              </Suspense>
            )}

            {/* Journal Tab - Combined Dashboard + Journal + Analytics */}
            {activeTab === 'journal' && (
              <Suspense fallback={<TabFallback />}>
              <div className="dashboard-grid" key={refreshKey}>
                {/* Account Manager */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <AccountManager onAccountChange={setSelectedAccount} selectedAccount={selectedAccount} />
                </div>
                
                {/* Dashboard Summary Stats */}
                <DashboardSummary 
                  key={`summary-${refreshKey}`}
                  exchangeRate={exchangeRate} 
                  onNavigate={(tab) => setActiveTab(tab)}
                  compact={true}
                />
                
                {/* Trading Journal */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <TradingJournal exchangeRate={exchangeRate} selectedAccount={selectedAccount} />
                </div>
                
                {/* Analytics Section - Without Equity Curve */}
                <SessionAnalysis exchangeRate={exchangeRate} />
                <EmotionAnalysis exchangeRate={exchangeRate} />
                <RiskHeatMap exchangeRate={exchangeRate} selectedAccount={selectedAccount} />
                
                {/* Export & Reports */}
                <WeeklyReport exchangeRate={exchangeRate} />
                <ExportData selectedAccount={selectedAccount} />
              </div>
              </Suspense>
            )}

          {/* Strategy Builder Tab */}
          {activeTab === 'strategy' && (
            <Suspense fallback={<TabFallback />}>
            <div className="dashboard-grid">
              <div style={{ gridColumn: '1 / -1' }}>
                <StrategyBuilder key={refreshKey} />
              </div>
            </div>
            </Suspense>
          )}

          {/* Achievements Tab */}
          {activeTab === 'achievements' && (
            <Suspense fallback={<TabFallback />}>
            <Achievements key={refreshKey} />
            </Suspense>
          )}

          {/* Feedback Tab */}
          {activeTab === 'feedback' && (
            <Suspense fallback={<TabFallback />}>
              <Feedback key={refreshKey} />
            </Suspense>
          )}

          {/* Trade Guardian Tab */}
          {activeTab === 'guardian' && (
            <Suspense fallback={<TabFallback />}>
              <TradeGuardian key={refreshKey} />
            </Suspense>
          )}

          {/* Smart Capital CopyTrade Tab */}
          {activeTab === 'smartcapital' && (
            <Suspense fallback={<TabFallback />}>
              <SmartCapital key={refreshKey} />
            </Suspense>
          )}

          {/* Premium Features Tab (with lazy loading) */}
          {activeTab === 'premium' && (
            <Suspense fallback={<LazyLoadFallback />}>
              <div className="dashboard-grid">
                <PremiumFeatures key={refreshKey} />
              </div>
            </Suspense>
          )}

          {/* Polymarket Prediction Tab */}
          {activeTab === 'polymarket' && (
            <Suspense fallback={<TabFallback />}>
              <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
                <PolymarketPrediction key={refreshKey} />
              </div>
            </Suspense>
          )}

          {/* AI Vision Analysis Tab */}
          {activeTab === 'aivision' && (
            <Suspense fallback={<TabFallback />}>
              <AIVisionAnalysis key={refreshKey} />
            </Suspense>
          )}

          {/* Fira Pro League — public trading contest leaderboard (Feb 2026)
              Available on BOTH MY and WW portals. Public-facing, no auth gate. */}
          {activeTab === 'proleague' && (
            <Suspense fallback={<TabFallback />}>
              <ProLeaguePage key={refreshKey} />
            </Suspense>
          )}

          {/* Fira Worldwide Pricing Page (WW only) */}
          {activeTab === 'pricing' && isWorldwide && (
            <Suspense fallback={<TabFallback />}>
              <WorldwidePricingPage key={refreshKey} />
            </Suspense>
          )}

          {/* Signal Performance Tab */}
          {activeTab === 'performance' && (
            <Suspense fallback={<TabFallback />}>
              <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
                <SignalPerformance key={refreshKey} />
              </div>
            </Suspense>
          )}

          {/* Wallet Tab */}
          {activeTab === 'wallet' && (
            <Suspense fallback={<TabFallback />}>
            <div style={{ 
              maxWidth: '600px', 
              margin: '0 auto', 
              width: '100%',
              background: 'var(--bg-card)',
              borderRadius: '16px',
              padding: '20px',
              border: '1px solid var(--border-subtle)'
            }}>
              <WalletDashboard key={refreshKey} user={user} />
            </div>
            </Suspense>
          )}

          {/* Tools Tab - Combined Tools & Calculators */}
          {activeTab === 'tools' && (
            <Suspense fallback={<TabFallback />}>
            <div className="dashboard-grid" key={refreshKey}>
              {/* Risk & Position Tools */}
              <RiskCalculator exchangeRate={exchangeRate} />
              <TargetTracker exchangeRate={exchangeRate} />
              <DrawdownAlert />
              
              {/* Calculators */}
              <PipValueCalculator exchangeRate={exchangeRate} />
              <CompoundingCalculator exchangeRate={exchangeRate} />
              <BreakEvenCalculator />
              
              {/* Market Tools */}
              <MarketSnapshot />
              <EconomicCalendar />
            </div>
            </Suspense>
          )}

          {/* AI Chat Tab */}
          {activeTab === 'ai' && (
            <Suspense fallback={<TabFallback />}>
            <div key={refreshKey} style={{ maxWidth: '900px', margin: '0 auto', width: '100%', overflow: 'hidden', padding: '0 8px', boxSizing: 'border-box' }}>
              <MarketSessionBanner />
              <AIChat />
            </div>
            </Suspense>
          )}

          {/* Education Tab */}
          {activeTab === 'education' && (
            <Suspense fallback={<TabFallback />}>
            <div className="dashboard-grid" key={refreshKey}>
              <div style={{ gridColumn: '1 / -1' }}>
                <TradingEducation />
              </div>
            </div>
            </Suspense>
          )}

          {/* Guide Tab */}
          {activeTab === 'guide' && (
            <Suspense fallback={<TabFallback />}>
            <div className="dashboard-grid">
              <div style={{ gridColumn: '1 / -1' }}>
                <FeatureGuide onGuideComplete={handleGuideComplete} />
              </div>
            </div>
            </Suspense>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <Suspense fallback={<TabFallback />}>
            <div className="dashboard-grid" key={refreshKey}>
              <div style={{ gridColumn: '1 / -1' }}>
                <AccountManager onAccountChange={setSelectedAccount} selectedAccount={selectedAccount} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <MT5Integration tradingAccounts={tradingAccounts} />
              </div>
            </div>
            </Suspense>
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <Suspense fallback={<TabFallback />}>
              <ProfileSettings onLogout={logout} />
            </Suspense>
          )}

          {/* Fallback for invalid tabs */}
          {!['guide', 'checklist', 'journal', 'ai', 'tools', 'premium', 'aivision', 'smartcapital', 'performance', 'polymarket', 'education', 'achievements', 'feedback', 'guardian', 'wallet', 'strategy', 'profile', 'proleague'].includes(activeTab) && (
            <div className="dashboard-grid" key={refreshKey}>
              <div className="module-card" style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔄</div>
                <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Loading...</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>
                  Redirecting...
                </p>
                <button 
                  onClick={() => setActiveTab('premium')}
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #FF4081, #F50057)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      </div>
      {showGuideReminder && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-card)',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '420px',
            width: '100%',
            textAlign: 'center',
            border: '1px solid var(--border-subtle)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{
              width: '70px',
              height: '70px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FF4081, #F50057)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              boxShadow: '0 8px 24px rgba(255, 64, 129, 0.4)'
            }}>
              <BookOpen size={32} color="#fff" />
            </div>
            
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: 'var(--text-primary)',
              marginBottom: '12px'
            }}>
              {t('language.toggle') === 'Tukar Bahasa' 
                ? 'Panduan Belum Selesai!' 
                : 'Guide Not Complete!'}
            </h3>
            
            <p style={{
              fontSize: '14px',
              color: 'var(--text-muted)',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              {t('language.toggle') === 'Tukar Bahasa'
                ? 'Kami sangat menggalakkan anda untuk selesaikan panduan dan kuiz dahulu supaya anda faham sepenuhnya cara guna Fira Smart Desk.'
                : 'We highly recommend completing the guide and quiz first so you fully understand how to use Fira Smart Desk.'}
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={handleBackToGuide}
                style={{
                  padding: '14px 24px',
                  background: 'linear-gradient(135deg, #FF4081, #F50057)',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <BookOpen size={18} />
                {t('language.toggle') === 'Tukar Bahasa'
                  ? 'Kembali ke Panduan'
                  : 'Back to Guide'}
              </button>
              
              <button
                onClick={handleSkipGuide}
                style={{
                  padding: '14px 24px',
                  background: 'transparent',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: 'var(--text-muted)'
                }}
              >
                {t('language.toggle') === 'Tukar Bahasa'
                  ? 'Skip Sekali Ini'
                  : 'Skip This Time'}
              </button>

              <button
                onClick={handleSkipGuidePermanently}
                style={{
                  padding: '10px 24px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  color: 'var(--text-muted)',
                  opacity: 0.7
                }}
              >
                {t('language.toggle') === 'Tukar Bahasa'
                  ? '✓ Jangan Tunjuk Lagi'
                  : "✓ Don't Show Again"}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Bottom Navigation - Mobile Only */}
      {isMobile && (
        <BottomNav 
          activeTab={activeTab} 
          onTabChange={handleTabChange}
          onMenuClick={() => setSidebarOpen(true)}
        />
      )}
    </div>
  );
}

function App() {
  const [showSystemGuideGlobal, setShowSystemGuideGlobal] = useState(false);
  
  // Global event listener for system guide
  useEffect(() => {
    const handleShowSystemGuide = () => setShowSystemGuideGlobal(true);
    window.addEventListener('showSystemGuide', handleShowSystemGuide);
    return () => window.removeEventListener('showSystemGuide', handleShowSystemGuide);
  }, []);
  
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
            <ToastProvider>
              <Routes>
                {/* Marketing Homepage + Embedded Login (SEO-optimized).
                    Legacy invite links use /?invite=CODE — redirect them to /login. */}
                <Route path="/" element={<HomeOrInviteRedirect />} />
                
                {/* Login/Register Page (accessible directly) */}
                <Route path="/login" element={<AuthPageWrapper />} />
                
                {/* Password Reset Page */}
                <Route path="/reset-password" element={<AuthPageWrapper />} />
                
                {/* Privacy Policy & Terms — Public pages for App Store */}
                <Route path="/privacy" element={<Suspense fallback={<div />}><PrivacyPolicy /></Suspense>} />
                <Route path="/terms" element={<Suspense fallback={<div />}><TermsOfService /></Suspense>} />
                <Route path="/about" element={<Suspense fallback={<div />}><AboutUs /></Suspense>} />
                
                {/* Feature deep-dive pages (SEO) */}
                <Route path="/features/ai-chart-vision" element={<Suspense fallback={<LazyLoadFallback />}><AIChartVisionPage /></Suspense>} />
                <Route path="/features/currency-strength-meter" element={<Suspense fallback={<LazyLoadFallback />}><CurrencyStrengthPage /></Suspense>} />
                <Route path="/features/premium-signals" element={<Suspense fallback={<LazyLoadFallback />}><PremiumSignalsPage /></Suspense>} />
                <Route path="/features/signal-performance" element={<Suspense fallback={<LazyLoadFallback />}><SignalPerformancePage /></Suspense>} />

                {/* Fira Pro League — public leaderboard route (no auth required).
                    Per Munir (Feb 2026) — shareability across Telegram/socmed.
                    Same component is also reachable inside the MY/WW dashboards
                    as the `proleague` tab; this exposes it on a bare public URL. */}
                <Route path="/pro-league" element={<Suspense fallback={<div />}><ProLeaguePage /></Suspense>} />

                {/* Protected Dashboard — portal-locked to main domain */}
                <Route path="/dashboard/*" element={<PortalLock expected="main"><WorldwideProvider isWorldwide={false}><AppContent /></WorldwideProvider></PortalLock>} />

                {/* Worldwide Portal — Fixed EN, limited features, portal-locked */}
                <Route path="/worldwide" element={<WorldwideForceEN><AuthPageWrapper worldwideMode /></WorldwideForceEN>} />
                <Route path="/worldwide/dashboard/*" element={<PortalLock expected="worldwide"><WorldwideForceEN><WorldwideProvider isWorldwide={true}><AppContent /></WorldwideProvider></WorldwideForceEN></PortalLock>} />
                
                {/* Redirect all unknown routes */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              
              {/* Global Live Support - Only on dashboard */}
              <SystemGuideModal 
                isOpen={showSystemGuideGlobal} 
                onClose={() => setShowSystemGuideGlobal(false)} 
              />
            </ToastProvider>
          </LanguageProvider>
        </ThemeProvider>
      </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

// Landing Page Wrapper - redirect to correct portal if logged in
function LandingPageWrapper() {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) return <LazyLoadFallback />;
  if (isAuthenticated) {
    // Route users to their portal: WW flagged users → /worldwide, others → /dashboard
    const dest = user?.is_worldwide ? '/worldwide/dashboard' : '/dashboard';
    return <Navigate to={dest} replace />;
  }

  return <LandingPage />;
}

/**
 * PortalLock — hard-isolates authenticated users to their correct portal.
 *   expected="worldwide" → only users with `is_worldwide===true` allowed; others redirect to /dashboard
 *   expected="main"      → only non-worldwide users allowed; WW users redirect to /worldwide/dashboard
 * This prevents UX/brand bleed (WW users seeing MY tabs or vice-versa).
 */
function PortalLock({ expected, children }) {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <LazyLoadFallback />;
  if (!isAuthenticated) {
    return <Navigate to={expected === 'worldwide' ? '/worldwide' : '/'} replace />;
  }
  const isWw = !!user?.is_worldwide;
  if (expected === 'worldwide' && !isWw) {
    return <Navigate to="/dashboard" replace />;
  }
  if (expected === 'main' && isWw) {
    return <Navigate to="/worldwide/dashboard" replace />;
  }
  return children;
}

// Auth Page Wrapper - redirect if already logged in
// Force EN language for worldwide portal — no toggle
function WorldwideForceEN({ children }) {
  const { language, setLanguage } = useLanguage();
  useEffect(() => {
    if (language !== 'en') {
      setLanguage('en');
      localStorage.setItem('fira-language', 'en');
    }
  }, []); // Run once on mount only
  return children;
}

function AuthPageWrapper({ worldwideMode = false }) {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LazyLoadFallback />;
  }

  if (isAuthenticated) {
    // If the logged-in user's portal doesn't match the auth page they're on,
    // redirect to THEIR portal. This kills cross-portal login attempts.
    const userIsWw = !!user?.is_worldwide;
    if (worldwideMode && !userIsWw) {
      return <Navigate to="/dashboard" replace />;
    }
    if (!worldwideMode && userIsWw) {
      return <Navigate to="/worldwide/dashboard" replace />;
    }
    const tabParam = new URLSearchParams(window.location.search).get('tab');
    const dashPath = userIsWw ? '/worldwide/dashboard' : '/dashboard';
    return <Navigate to={tabParam ? `${dashPath}?tab=${tabParam}` : dashPath} replace />;
  }

  return <AuthPage worldwideMode={worldwideMode} />;
}

export default App;
